/**
 * Fixed Routes Statistics Component
 * 
 * Displays comprehensive statistics about the fixed route network
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Chip,
  LinearProgress,
} from '@mui/material';

import { useFixedRoutes } from '../../context/FixedRoutesContext';
import type { FixedRoute, FixedRouteStop } from '../../types/fixedRoutes';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { FixedRoutesCostSection } from './FixedRoutesCostSection';
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, subtitle, color = 'primary' }) => {
  const colorMap = {
    primary: '#1976d2',
    secondary: '#9c27b0',
    success: '#2e7d32',
    error: '#d32f2f',
    info: '#0288d1',
    warning: '#ed6c02',
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            color: colorMap[color],
            mr: 1.5,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: colorMap[color],
          mb: subtitle ? 0.5 : 0,
        }}
      >
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
};

const parseTimeToMinutes = (timeStr: string): number | null => {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  if (parts.length < 2) return null;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (isNaN(hours) || isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const formatTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

export function FixedRoutesStatistics() {
  const { t } = useTranslation('fixedRoutes');
  const { routes, stops } = useFixedRoutes();

  const stats = useMemo(() => {
    if (routes.length === 0) {
      return null;
    }

    // Basic counts
    const totalRoutes = routes.length;
    const circulatorRoutes = routes.filter((r) => r.isCirculator).length;
    const regularRoutes = totalRoutes - circulatorRoutes;

    // Group stops by name and coordinates (same logic as StopManager)
    const stopGroups = new Map<string, {
      stop: FixedRouteStop;
      stopIds: string[];
      routeIds: Set<string>;
    }>();

    stops.forEach((stop) => {
      const coordsKey = stop.coordinates
        ? `${stop.coordinates.lat.toFixed(4)},${stop.coordinates.lng.toFixed(4)}`
        : 'no-coords';
      const groupKey = `${stop.name.toLowerCase()}|${coordsKey}`;
      
      if (!stopGroups.has(groupKey)) {
        stopGroups.set(groupKey, {
          stop,
          stopIds: [stop.id],
          routeIds: new Set<string>(),
        });
      } else {
        const group = stopGroups.get(groupKey)!;
        if (!group.stopIds.includes(stop.id)) {
          group.stopIds.push(stop.id);
        }
      }
    });

    // Track which stop groups are used in routes
    routes.forEach((route) => {
      route.stops.forEach((stopConfig) => {
        // Find which group this stop ID belongs to
        stopGroups.forEach((group) => {
          if (group.stopIds.includes(stopConfig.stopId)) {
            group.routeIds.add(route.id);
          }
        });
      });
    });

    // Count total unique stop groups (used in routes)
    const totalStopsUsed = Array.from(stopGroups.values()).filter(
      (group) => group.routeIds.size > 0
    ).length;
    
    // Count all unique stop groups in context (including unused)
    const totalStopsInContext = stopGroups.size;
    
    // Count unused stop groups (in context but not in any route)
    const unusedStops = Array.from(stopGroups.values()).filter(
      (group) => group.routeIds.size === 0
    ).length;

    // Operating hours
    let earliestStart: number | null = null;
    let latestEnd: number | null = null;
    const operatingHours: number[] = [];

    routes.forEach((route) => {
      const start = parseTimeToMinutes(route.startTime);
      const end = parseTimeToMinutes(route.endTime || route.startTime);

      if (start !== null) {
        if (earliestStart === null || start < earliestStart) {
          earliestStart = start;
        }
        operatingHours.push(start);
      }

      if (end !== null) {
        if (latestEnd === null || end > latestEnd) {
          latestEnd = end;
        }
        operatingHours.push(end);
      }
    });

    const operatingSpan = earliestStart !== null && latestEnd !== null ? latestEnd - earliestStart : 0;

    // Route statistics
    const stopsPerRoute = routes.map((r) => r.stops.length);
    const avgStopsPerRoute = stopsPerRoute.reduce((a, b) => a + b, 0) / stopsPerRoute.length;
    const maxStops = Math.max(...stopsPerRoute, 0);
    const minStops = Math.min(...stopsPerRoute, 0);

    // Route durations (from segments)
    const routeDurations = routes.map((route) => {
      const totalTime = route.segments.reduce((sum, seg) => sum + seg.travelTimeMinutes, 0);
      const waitTime = route.stops.reduce((sum, stop) => sum + stop.waitTimeMinutes, 0);
      return totalTime + waitTime;
    });

    const avgRouteDuration = routeDurations.reduce((a, b) => a + b, 0) / routeDurations.length;
    const maxRouteDuration = Math.max(...routeDurations, 0);
    const minRouteDuration = Math.min(...routeDurations, 0);

    // Stop usage (grouped by name and coordinates)
    const stopUsage = Array.from(stopGroups.values())
      .filter((group) => group.routeIds.size > 0)
      .map((group) => ({
        stop: group.stop,
        count: group.routeIds.size,
        instanceCount: group.stopIds.length,
      }));

    const mostUsedStops = stopUsage
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)
      .map((item) => ({
        stop: item.stop,
        count: item.count,
        instanceCount: item.instanceCount,
      }));

    const leastUsedStops = stopUsage
      .sort((a, b) => a.count - b.count)
      .slice(0, 5)
      .map((item) => ({
        stop: item.stop,
        count: item.count,
        instanceCount: item.instanceCount,
      }));

    // Total segments
    const totalSegments = routes.reduce((sum, r) => sum + r.segments.length, 0);

    // Time distribution (group by hour)
    const hourlyDistribution = new Map<number, number>();
    operatingHours.forEach((minutes) => {
      const hour = Math.floor(minutes / 60) % 24;
      hourlyDistribution.set(hour, (hourlyDistribution.get(hour) || 0) + 1);
    });

    const peakHour = Array.from(hourlyDistribution.entries())
      .sort((a, b) => b[1] - a[1])[0];

    const coveragePercent = totalStopsInContext > 0 ? (totalStopsUsed / totalStopsInContext) * 100 : 0;

    // Calculate capacity heuristics per route
    const routeCapacities = routes.map((route) => {
      const DEFAULT_CAPACITY = 50; // Default vehicle capacity if not specified
      const capacity = route.vehicleCapacity ?? DEFAULT_CAPACITY; // Use nullish coalescing to allow 0
      
      let dailyCapacity = 0;
      let hourlyCapacity = 0;
      
      if (route.isCirculator && route.vehicleCount && route.vehicleCount >= 1) {
        // Circulator route capacity calculation
        const startMinutes = parseTimeToMinutes(route.startTime) || 0;
        const endMinutes = parseTimeToMinutes(route.endTime || route.startTime) || 0;
        const operatingMinutes = endMinutes > startMinutes 
          ? endMinutes - startMinutes 
          : (24 * 60) - startMinutes + endMinutes; // Handle overnight
        
        // Calculate loop duration
        const loopDuration = route.segments.reduce((sum, seg) => sum + seg.travelTimeMinutes, 0) +
          route.stops.reduce((sum, stop) => sum + stop.waitTimeMinutes, 0);
        
        if (loopDuration > 0) {
          // Daily capacity: trips per day × capacity
          const tripsPerDay = (operatingMinutes / loopDuration) * route.vehicleCount;
          dailyCapacity = Math.round(tripsPerDay * capacity);
          
          // Hourly capacity: trips per hour × capacity
          const headway = loopDuration / route.vehicleCount; // minutes between vehicles
          const tripsPerHour = 60 / headway;
          hourlyCapacity = Math.round(tripsPerHour * capacity);
        }
      } else if (route.tripSchedules && route.tripSchedules.length > 0) {
        // Scheduled route capacity calculation
        const tripsPerDay = route.tripSchedules.length;
        dailyCapacity = tripsPerDay * capacity;
        
        // Calculate hourly capacity: find peak hour
        const tripsByHour = new Map<number, number>();
        route.tripSchedules.forEach((trip) => {
          const hour = Math.floor(trip.startTimeMinutes / 60) % 24;
          tripsByHour.set(hour, (tripsByHour.get(hour) || 0) + 1);
        });
        
        const maxTripsInHour = Math.max(...Array.from(tripsByHour.values()), 0);
        hourlyCapacity = maxTripsInHour * capacity;
      } else {
        // Single trip route (non-circulator, no schedules)
        dailyCapacity = capacity;
        hourlyCapacity = capacity;
      }
      
      return {
        routeId: route.id,
        routeName: route.name,
        dailyCapacity,
        hourlyCapacity,
        vehicleCapacity: capacity,
      };
    });

    return {
      totalRoutes,
      totalStops: totalStopsUsed, // Count of unique stops used in routes
      totalStopsInContext, // Total stops in context (including unused)
      unusedStops, // Stops in context but not used in any route
      circulatorRoutes,
      regularRoutes,
      earliestStart,
      latestEnd,
      operatingSpan,
      avgStopsPerRoute,
      maxStops,
      minStops,
      avgRouteDuration,
      maxRouteDuration,
      minRouteDuration,
      mostUsedStops,
      leastUsedStops,
      totalSegments,
      peakHour,
      uniqueStopsUsed: totalStopsUsed,
      coveragePercent,
      routeCapacities,
    };
  }, [routes, stops]);

  if (!stats) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {t('statistics.emptyMessage')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        {t('statistics.networkStatistics')}
      </Typography>

      {/* Primary Stats Grid */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }}
        gap={2}
        sx={{ mb: 3 }}
      >
        <StatCard
          icon={<Icon size={20} name="bus" />}
          label={t('statistics.totalRoutes')}
          value={stats.totalRoutes}
          subtitle={t('statistics.totalRoutesSubtitle', {
            count: stats.circulatorRoutes,
            circulators: stats.circulatorRoutes,
            regular: stats.regularRoutes,
          })}
          color="primary"
        />
        <StatCard
          icon={<Icon size={20} name="location-dot" />}
          label={t('statistics.totalStops')}
          value={stats.totalStops}
          subtitle={
            stats.unusedStops > 0
              ? t('statistics.totalStopsInContextWithUnused', { total: stats.totalStopsInContext, unused: stats.unusedStops })
              : t('statistics.totalStopsInContext', { total: stats.totalStopsInContext })
          }
          color="info"
        />
        <StatCard
          icon={<Icon size={20} name="clock" />}
          label={t('statistics.operatingHours')}
          value={
            stats.earliestStart !== null && stats.latestEnd !== null
              ? `${formatTime(stats.earliestStart)} - ${formatTime(stats.latestEnd)}`
              : t('common.notAvailable')
          }
          subtitle={formatDuration(stats.operatingSpan)}
          color="success"
        />
        <StatCard
          icon={<Icon size={20} name="route" />}
          label={t('statistics.totalSegments')}
          value={stats.totalSegments}
          subtitle={t('statistics.segmentsAvgSubtitle', { avg: (stats.totalSegments / stats.totalRoutes).toFixed(1) })}
          color="secondary"
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Route Details */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {t('statistics.routeDetails')}
      </Typography>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
        gap={2}
        sx={{ mb: 3 }}
      >
        <StatCard
          icon={<Icon size={20} d={ICON_FALLBACKS.TrendingUp} />}
          label={t('statistics.avgStopsPerRoute')}
          value={stats.avgStopsPerRoute.toFixed(1)}
          subtitle={t('statistics.maxMinSubtitle', { max: stats.maxStops, min: stats.minStops })}
          color="info"
        />
        <StatCard
          icon={<Icon size={20} name="clock" />}
          label={t('statistics.avgRouteDuration')}
          value={formatDuration(Math.round(stats.avgRouteDuration))}
          subtitle={t('statistics.maxMinDurationSubtitle', { max: formatDuration(stats.maxRouteDuration), min: formatDuration(stats.minRouteDuration) })}
          color="warning"
        />
        <StatCard
          icon={<Icon size={20} name="map" />}
          label={t('statistics.stopCoverage')}
          value={`${stats.coveragePercent.toFixed(1)}%`}
          subtitle={t('statistics.stopCoverageSubtitle', { used: stats.totalStops, total: stats.totalStopsInContext })}
          color="success"
        />
      </Box>

      {/* Peak Hour */}
      {stats.peakHour && (
        <>
          <Divider sx={{ my: 3 }} />
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Icon size={20} d={ICON_FALLBACKS.SwapHoriz} style={{ marginRight: 8, color: '#ef7335' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('statistics.peakActivityHour')}
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {formatTime(stats.peakHour[0] * 60)}:00
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('statistics.routesActiveAtHour', { count: stats.peakHour[1] })}
            </Typography>
          </Paper>
        </>
      )}

      {/* Stop Usage */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {t('statistics.stopUsage')}
      </Typography>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }}
        gap={2}
      >
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            {t('statistics.mostUsedStops')}
          </Typography>
          {stats.mostUsedStops.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {stats.mostUsedStops.map((item, idx) => (
                <Box key={item.stop!.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Chip label={idx + 1} size="small" color="primary" />
                    <Typography variant="body2">{item.stop!.name}</Typography>
                    {item.instanceCount > 1 && (
                      <Chip
                        label={t('statistics.instances', { count: item.instanceCount })}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {t('statistics.routesUsedIn', { count: item.count })}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('common.noDataAvailable')}
            </Typography>
          )}
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            {t('statistics.leastUsedStops')}
          </Typography>
          {stats.leastUsedStops.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {stats.leastUsedStops.map((item, idx) => (
                <Box key={item.stop!.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Chip label={idx + 1} size="small" color="default" />
                    <Typography variant="body2">{item.stop!.name}</Typography>
                    {item.instanceCount > 1 && (
                      <Chip
                        label={t('statistics.instances', { count: item.instanceCount })}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('statistics.routesUsedIn', { count: item.count })}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('common.noDataAvailable')}
            </Typography>
          )}
        </Paper>
      </Box>

      {/* Capacity Summary Section */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {t('statistics.capacityHeuristics')}
      </Typography>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
        gap={2}
        sx={{ mb: 3 }}
      >
        {stats.routeCapacities.map((routeCap) => (
          <Paper key={routeCap.routeId} sx={{ p: 2 }} elevation={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              {routeCap.routeName}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('statistics.dailyCapacity')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {t('statistics.passengers', { value: routeCap.dailyCapacity.toLocaleString() })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('statistics.hourlyCapacity')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {t('statistics.passengers', { value: routeCap.hourlyCapacity.toLocaleString() })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  {t('statistics.vehicleCapacityLabel')}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {t('statistics.passengersPerVehicle', { value: routeCap.vehicleCapacity })}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Operating cost section (rate editor + network rollup + per-route costs/overrides) */}
      <FixedRoutesCostSection />
    </Box>
  );
}

